import React from 'react';

import {
  Alert,
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  InputLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Block as BlockIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import {useTheme} from '@mui/material/styles';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useIntl, defineMessages} from 'react-intl';

import API, {Types, VehicleGetProps, useCsrfToken} from '@wheeebo/polaris-api';
import {useSnackbar} from '@wheeebo/polaris-snackbar';
import {
  CircleLoader,
  ConfirmationDialog,
  GroupPanel,
  LoadError,
  ManualControlPad,
  MemberPanel,
  SquareChip,
  VehicleCommandForm,
  VehiclePanel,
  useMonitorMap,
  useErrorHandler,
  useMap,
} from '@wheeebo/polaris-ui';

import GroupDialog from './GroupDialog';
import MemberDialog from './MemberDialog';
import MissionDialog from './MissionDialog';
import VehicleDialog from './VehicleDialog';
import FlightPathDialog from './FlightPathDialog';

const INITIAL_ITEM_COUNT = 25;
const ACTIVE_VEHICLE_TIME_THRESHOLD = 10000; // 10 seconds

const msgs = defineMessages({
  errorNoFlightPlan: {
    id: 'pages.member.mission.FlightPlanCard.error.noFlightPlan',
    defaultMessage: 'Flight plan not found',
  },
  errorVehiecleNotSelected: {
    id: 'pages.member.mission.FlightPlanCard.error.vehiecleNotSelected',
    defaultMessage: 'Vehicle not selected',
  },
  buttonDelete: {
    id: 'pages.member.mission.FlightPlanCard.button.delete',
    defaultMessage: 'Delete',
  },
  buttonEdit: {
    id: 'pages.member.mission.FlightPlanCard.button.edit',
    defaultMessage: 'Edit',
  },
  buttonView: {
    id: 'pages.member.mission.FlightPlanCard.button.view',
    defaultMessage: 'View details',
  },
  dialogConfirmationTitle: {
    id: 'pages.member.mission.FlightPlanCard.dialog.confirmationTitle',
    defaultMessage: 'Confirm',
  },
  dialogDeleteConfirmationContent: {
    id: 'pages.member.mission.FlightPlanCard.dialog.deleteConfirmationContent',
    defaultMessage: 'Are you really going to delete this flight plan?',
  },
  dialogExecuteConfirmationContent: {
    id: 'pages.member.mission.FlightPlanCard.dialog.executeConfirmationContent',
    defaultMessage: 'Are you really going to execute this flight plan right now?',
  },
  dialogPauseConfirmationContent: {
    id: 'pages.member.mission.FlightPlanCard.dialog.pauseConfirmationContent',
    defaultMessage: 'Are you really going to pause this flight plan right now?',
  },
  dialogResumeConfirmationContent: {
    id: 'pages.member.mission.FlightPlanCard.dialog.resumeConfirmationContent',
    defaultMessage: 'Are you really going to resume this flight plan right now?',
  },
  dialogAbortConfirmationContent: {
    id: 'pages.member.mission.FlightPlanCard.dialog.abortConfirmationContent',
    defaultMessage: 'Are you really going to abort this flight plan right now?',
  },
  headerTitle: {
    id: 'pages.member.mission.FlightPlanCard.header.title',
    defaultMessage: 'Flight Plans',
  },
  headerSubTitle: {
    id: 'pages.member.mission.FlightPlanCard.header.subTitle',
    defaultMessage: 'List of flight plans',
  },
  successFlightPlanCreated: {
    id: 'pages.member.mission.FlightPlanCard.success.flightPlanCreated',
    defaultMessage: 'Flight plan created',
  },
  successFlightPlanDeleted: {
    id: 'pages.member.mission.FlightPlanCard.success.flightPlanDeleted',
    defaultMessage: 'Flight plan deleted',
  },
  successFlightPlanAborted: {
    id: 'pages.member.mission.FlightPlanCard.success.flightPlanAborted',
    defaultMessage: 'Flight plan aborted',
  },
  successFlightPlanExecuted: {
    id: 'pages.member.mission.FlightPlanCard.success.flightPlanExecuted',
    defaultMessage: 'Flight plan executed',
  },
  successFlightPlanPaused: {
    id: 'pages.member.mission.FlightPlanCard.success.flightPlanPaused',
    defaultMessage: 'Flight plan paused',
  },
  successFlightPlanResumed: {
    id: 'pages.member.mission.FlightPlanCard.success.flightPlanResumed',
    defaultMessage: 'Flight plan resumed',
  },
});

interface RowProps {
  vehicleId: Types.UUID;
  selected: boolean;
  coords: google.maps.LatLngLiteral;
  onPause: (flightPlanId: Types.UUID) => void;
  onResume: (flightPlanId: Types.UUID) => void;
  onAbort: (flightPlanId: Types.UUID) => void;
  onSelected: () => void;
}

function Row({vehicleId, selected, coords, onPause, onResume, onAbort, onSelected}: RowProps) {
  const intl = useIntl();
  const theme = useTheme();
  const [selectedMemberId, setSelectedMemberId] = React.useState<Types.UUID>();
  const [selectedGroupId, setSelectedGroupId] = React.useState<Types.UUID>();
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<Types.UUID>();
  const [selectedMissionId, setSelectedMissionId] = React.useState<Types.UUID>();
  const [selectedFlightPathId, setSelectedFlightPathId] = React.useState<Types.UUID>();
  const options: VehicleGetProps = {
    preload: selected
      ? ['owner', 'operator', 'flight_path', 'mission', 'group', 'active_flight_plan', 'last_flight_log']
      : ['last_flight_log'],
  };
  const {
    data: vehicle,
    isLoading,
    isError,
  } = useQuery(API.Vehicles.getKey(vehicleId, options), () => API.Vehicles.get(vehicleId, options));
  const activeFlightPlan = vehicle?.activeFlightPlan;
  const lastFlightLog = vehicle?.activeFlightPlan?.lastFlightLog;
  const commandAvailable =
    vehicle?.vehicleType === Types.VehicleTypeDrone &&
    Date.now() - vehicle?.lastHeartbeatAt < ACTIVE_VEHICLE_TIME_THRESHOLD &&
    (!vehicle?.activeFlightPlan ||
      lastFlightLog?.status === Types.FlightLogStatusInitialized ||
      lastFlightLog?.status === Types.FlightLogStatusCompleted ||
      lastFlightLog?.status === Types.FlightLogStatusIncompleted);
  const handleMemberSelected = React.useCallback((memberId: Types.UUID) => {
    setSelectedMemberId(memberId);
    setSelectedGroupId(undefined);
    setSelectedVehicleId(undefined);
    setSelectedMissionId(undefined);
    setSelectedFlightPathId(undefined);
  }, []);
  const handleGroupSelected = React.useCallback((groupId: Types.UUID) => {
    setSelectedMemberId(undefined);
    setSelectedGroupId(groupId);
    setSelectedVehicleId(undefined);
    setSelectedMissionId(undefined);
    setSelectedFlightPathId(undefined);
  }, []);
  const handleVehicleSelected = React.useCallback((vehicleId: Types.UUID) => {
    setSelectedMemberId(undefined);
    setSelectedGroupId(undefined);
    setSelectedVehicleId(vehicleId);
    setSelectedMissionId(undefined);
    setSelectedFlightPathId(undefined);
  }, []);
  const handleMissionSelected = React.useCallback((missionId: Types.UUID) => {
    setSelectedMemberId(undefined);
    setSelectedGroupId(undefined);
    setSelectedVehicleId(undefined);
    setSelectedMissionId(missionId);
    setSelectedFlightPathId(undefined);
  }, []);
  const handleFlightPathSelected = React.useCallback((_: Types.UUID, flightPathId: Types.UUID) => {
    setSelectedMemberId(undefined);
    setSelectedGroupId(undefined);
    setSelectedVehicleId(undefined);
    setSelectedMissionId(undefined);
    setSelectedFlightPathId(flightPathId);
  }, []);
  if (isLoading) {
    return (
      <TableRow>
        <TableCell>
          <CircleLoader />
        </TableCell>
      </TableRow>
    );
  } else if (isError) {
    return (
      <TableRow>
        <TableCell>
          <LoadError />
        </TableCell>
      </TableRow>
    );
  } else if (!vehicle) {
    return (
      <TableRow>
        <TableCell>No vehicle found.</TableCell>
      </TableRow>
    );
  }
  return (
    <>
      <TableRow hover onClick={onSelected} sx={{cursor: 'pointer'}}>
        <TableCell
          sx={{
            p: 1,
            borderLeftWidth: 3,
            borderLeftStyle: 'solid',
            borderLeftColor:
              lastFlightLog?.status === Types.FlightLogStatusExecuting
                ? theme.palette.error.light
                : lastFlightLog?.status === Types.FlightLogStatusPaused
                ? theme.palette.info.light
                : lastFlightLog?.status === Types.FlightLogStatusCompleted
                ? theme.palette.success.light
                : lastFlightLog?.status === Types.FlightLogStatusIncompleted
                ? theme.palette.warning.light
                : 'transparent',
          }}>
          <VehiclePanel vehicle={vehicle} showFlightStatus color={selected ? theme.palette.warning.light : undefined} />
        </TableCell>
        <TableCell align="right" sx={{minWidth: 30}}>
          {selected ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} sx={{paddingBottom: selected ? undefined : 0, paddingTop: selected ? undefined : 0}}>
          <Collapse in={selected}>
            <Grid container spacing={1}>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Vehicle Type
              </Grid>
              <Grid item xs={8} sx={{whiteSpace: 'pre'}}>
                <SquareChip label={vehicle.vehicleType} />
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Location
              </Grid>
              <Grid item xs={8} sx={{whiteSpace: 'pre'}}>
                {`${coords.lat.toFixed(5)}     ${coords.lng.toFixed(5)}`}
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Owner
              </Grid>
              <Grid item xs={8}>
                {vehicle?.owner?.id ? (
                  <MemberPanel member={vehicle.owner} onClick={() => setSelectedMemberId(vehicle.owner?.id)} />
                ) : (
                  'N/A'
                )}
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Operator
              </Grid>
              <Grid item xs={8}>
                {activeFlightPlan?.operator?.id ? (
                  <MemberPanel
                    member={activeFlightPlan.operator}
                    onClick={() => setSelectedMemberId(activeFlightPlan.operator.id)}
                  />
                ) : (
                  'N/A'
                )}
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Flight Started
              </Grid>
              <Grid item xs={8}>
                {intl.formatDate(lastFlightLog?.startAt, {dateStyle: 'short', timeStyle: 'short'})}
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Flight Path
              </Grid>
              <Grid
                item
                xs={8}
                sx={{cursor: 'pointer', textDecoration: 'underline'}}
                onClick={() =>
                  activeFlightPlan?.flightPath ? setSelectedFlightPathId(activeFlightPlan?.flightPath?.id) : undefined
                }>
                {activeFlightPlan?.flightPath?.name ?? 'N/A'}
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Mission
              </Grid>
              <Grid
                item
                xs={8}
                sx={{cursor: 'pointer', textDecoration: 'underline'}}
                onClick={() =>
                  activeFlightPlan?.flightPath?.mission?.id
                    ? setSelectedMissionId(activeFlightPlan?.flightPath?.mission?.id)
                    : undefined
                }>
                {activeFlightPlan?.flightPath?.mission?.name ?? 'N/A'}
              </Grid>
              <Grid item xs={4} sx={{display: 'flex', alignItems: 'center'}}>
                Group
              </Grid>
              <Grid item xs={8}>
                {activeFlightPlan?.flightPath?.mission.group?.name ? (
                  <GroupPanel
                    group={activeFlightPlan.flightPath.mission.group}
                    onClick={() => setSelectedGroupId(activeFlightPlan.flightPath.mission.group.id)}
                  />
                ) : (
                  'N/A'
                )}
              </Grid>
              {activeFlightPlan && lastFlightLog ? (
                <Grid item xs={12}>
                  <Stack direction="row" gap={1} sx={{justifyContent: 'flex-end'}}>
                    {lastFlightLog.status === Types.FlightLogStatusExecuting ? (
                      <Button startIcon={<PauseIcon />} color="info" onClick={() => onPause(activeFlightPlan.id)}>
                        Pause
                      </Button>
                    ) : lastFlightLog.status === Types.FlightLogStatusPaused ? (
                      <Button
                        startIcon={<PlayArrowIcon />}
                        color="success"
                        onClick={() => onResume(activeFlightPlan.id)}>
                        Resume
                      </Button>
                    ) : null}
                    {lastFlightLog.status === Types.FlightLogStatusExecuting ||
                    lastFlightLog.status === Types.FlightLogStatusPaused ? (
                      <Button startIcon={<BlockIcon />} color="error" onClick={() => onAbort(activeFlightPlan.id)}>
                        Abort
                      </Button>
                    ) : null}
                  </Stack>
                </Grid>
              ) : null}
              {vehicle.vehicleType === Types.VehicleTypeDrone ? (
                <Grid item xs={12}>
                  {commandAvailable ? (
                    <VehicleCommandForm vehicle={vehicle} />
                  ) : (
                    <Alert severity="info" variant="outlined">
                      To send a command to the vehicle, the active mission should be completed or aborted.
                    </Alert>
                  )}
                </Grid>
              ) : null}
              {vehicle.vehicleType === Types.VehicleTypeAsv ? (
                <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center'}}>
                  <ManualControlPad vehicle={vehicle} />
                </Grid>
              ) : null}
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
      {selectedMemberId ? (
        <MemberDialog
          open={!!selectedMemberId}
          memberId={selectedMemberId}
          onGroupSelected={handleGroupSelected}
          onMissionSelected={handleMissionSelected}
          onVehicleSelected={handleVehicleSelected}
          onFlightPathSelected={handleFlightPathSelected}
          onClose={() => setSelectedMemberId(undefined)}
        />
      ) : null}
      {selectedGroupId ? (
        <GroupDialog
          open={!!selectedGroupId}
          groupId={selectedGroupId}
          onMemberSelected={handleMemberSelected}
          onVehicleSelected={handleVehicleSelected}
          onClose={() => setSelectedGroupId(undefined)}
        />
      ) : null}
      {selectedVehicleId ? (
        <VehicleDialog
          open={!!selectedVehicleId}
          vehicleId={selectedVehicleId}
          onMemberSelected={handleMemberSelected}
          onGroupSelected={handleGroupSelected}
          onClose={() => setSelectedVehicleId(undefined)}
        />
      ) : null}
      {selectedMissionId ? (
        <MissionDialog
          open={!!selectedMissionId}
          missionId={selectedMissionId}
          onGroupSelected={handleGroupSelected}
          onClose={() => setSelectedMissionId(undefined)}
        />
      ) : null}
      {selectedFlightPathId ? (
        <FlightPathDialog
          open={!!selectedFlightPathId}
          flightPathId={selectedFlightPathId}
          onMissionSelected={handleMissionSelected}
          onClose={() => setSelectedFlightPathId(undefined)}
        />
      ) : null}
    </>
  );
}

function VehicleListPanel(): JSX.Element {
  const intl = useIntl();
  const {csrfToken} = useCsrfToken();
  const {showAndEmitJsErrorEvent} = useErrorHandler();
  const {showSuccess} = useSnackbar();
  const queryClient = useQueryClient();
  const {setCenter} = useMap();
  const {vehicles, selectedVehicleId, setSelectedVehicleId} = useMonitorMap();
  const [page, setPage] = React.useState(0);
  const [selectedFlightPlanId, setSelectedFlightPlanId] = React.useState<Types.UUID>();
  const [pauseDialogOpen, setPauseDialogOpen] = React.useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = React.useState(false);
  const [abortDialogOpen, setAbortDialogOpen] = React.useState(false);
  const {mutate: abortFlightPlan} = useMutation<unknown, unknown>(
    () => {
      setAbortDialogOpen(false);
      if (!selectedFlightPlanId) {
        return Promise.reject(intl.formatMessage(msgs.errorNoFlightPlan));
      }
      return API.FlightPlans.abort(csrfToken, selectedFlightPlanId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.FlightPlans.CacheKey);
        showSuccess(intl.formatMessage(msgs.successFlightPlanAborted));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const {mutate: pauseFlightPlan} = useMutation<unknown, unknown>(
    () => {
      setPauseDialogOpen(false);
      if (!selectedFlightPlanId) {
        return Promise.reject(intl.formatMessage(msgs.errorNoFlightPlan));
      }
      return API.FlightPlans.pause(csrfToken, selectedFlightPlanId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.FlightPlans.CacheKey);
        showSuccess(intl.formatMessage(msgs.successFlightPlanPaused));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  const {mutate: resumeFlightPlan} = useMutation<unknown, unknown>(
    () => {
      setResumeDialogOpen(false);
      if (!selectedFlightPlanId) {
        return Promise.reject(intl.formatMessage(msgs.errorNoFlightPlan));
      }
      return API.FlightPlans.resume(csrfToken, selectedFlightPlanId);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(API.FlightPlans.CacheKey);
        showSuccess(intl.formatMessage(msgs.successFlightPlanResumed));
      },
      onError: showAndEmitJsErrorEvent,
    },
  );
  return (
    <Card>
      <CardContent sx={{py: '8px !important'}}>
        <InputLabel shrink>Vehicles</InputLabel>
        {vehicles && vehicles.length > 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                {vehicles.map((e) => {
                  const pos = e.path.at(-1);
                  if (!pos) {
                    return null;
                  }
                  const coords = {lat: pos.lat, lng: pos.lng};
                  const selected = selectedVehicleId === e.vehicle.id;
                  return (
                    <Row
                      key={e.vehicle.id}
                      vehicleId={e.vehicle.id}
                      selected={selected}
                      coords={coords}
                      onPause={(flightPathId: Types.UUID) => {
                        setSelectedFlightPlanId(flightPathId);
                        setPauseDialogOpen(true);
                      }}
                      onResume={(flightPathId: Types.UUID) => {
                        setSelectedFlightPlanId(flightPathId);
                        setResumeDialogOpen(true);
                      }}
                      onAbort={(flightPathId: Types.UUID) => {
                        setSelectedFlightPlanId(flightPathId);
                        setAbortDialogOpen(true);
                      }}
                      onSelected={() => {
                        setSelectedVehicleId?.(e.vehicle.id);
                        setCenter?.(coords);
                      }}
                    />
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{
                      '.MuiTablePagination-displayedRows': {m: 1},
                    }}
                    backIconButtonProps={{sx: {p: 0}}}
                    nextIconButtonProps={{sx: {p: 0}}}
                    rowsPerPageOptions={[INITIAL_ITEM_COUNT]}
                    count={vehicles?.length || 0}
                    rowsPerPage={INITIAL_ITEM_COUNT}
                    page={page}
                    SelectProps={{
                      inputProps: {'aria-label': 'rows per page'},
                    }}
                    onPageChange={(_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="caption">No vehicle found</Typography>
        )}
        <ConfirmationDialog
          open={pauseDialogOpen}
          title={intl.formatMessage(msgs.dialogConfirmationTitle)}
          description={intl.formatMessage(msgs.dialogPauseConfirmationContent)}
          onCancel={() => setPauseDialogOpen(false)}
          onConfirm={() => pauseFlightPlan()}
        />
        <ConfirmationDialog
          open={resumeDialogOpen}
          title={intl.formatMessage(msgs.dialogConfirmationTitle)}
          description={intl.formatMessage(msgs.dialogResumeConfirmationContent)}
          onCancel={() => setResumeDialogOpen(false)}
          onConfirm={() => resumeFlightPlan()}
        />
        <ConfirmationDialog
          open={abortDialogOpen}
          title={intl.formatMessage(msgs.dialogConfirmationTitle)}
          description={intl.formatMessage(msgs.dialogAbortConfirmationContent)}
          onCancel={() => setAbortDialogOpen(false)}
          onConfirm={() => abortFlightPlan()}
        />
      </CardContent>
    </Card>
  );
}

export default React.memo(VehicleListPanel);
